import React from 'react';
import {CalendarIcon} from '@heroicons/react/24/outline';

export interface DateRangePickerProps {
  /**
   * The start date as string (e.g., "2026-01-16")
   */
  startDate: string | null;

  /**
   * The end date as string (e.g., "2026-01-20")
   */
  endDate: string | null;

  /**
   * Callback when either date changes
   */
  onChange: (startDate: string | null, endDate: string | null) => void;

  /**
   * ID for the start date input (accessibility)
   */
  startDateId?: string;

  /**
   * ID for the end date input (accessibility)
   */
  endDateId?: string;

  /**
   * Whether the inputs are disabled
   */
  disabled?: boolean;
}

/**
 * DateRangePicker component for selecting a date range (start and end dates).
 * Uses native HTML5 date inputs for best mobile support.
 *
 * Layout: Side by side on desktop, stacked on mobile for better UX.
 * Validates that end date is not before start date.
 */
export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  startDateId = 'start-date',
  endDateId = 'end-date',
  disabled = false,
}) => {
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value || null;

    // If end date exists and is before new start date, adjust end date
    if (newStartDate && endDate && endDate < newStartDate) {
      onChange(newStartDate, newStartDate);
    } else {
      onChange(newStartDate, endDate);
    }
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEndDate = e.target.value || null;

    // If start date exists and new end date is before it, adjust start date
    if (newEndDate && startDate && newEndDate < startDate) {
      onChange(newEndDate, newEndDate);
    } else {
      onChange(startDate, newEndDate);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-3">
      {/* Start Date Input */}
      <div className="flex-1">
        <label htmlFor={startDateId} className="block text-xs  text-muted mb-1.5">
          Start Date
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <CalendarIcon className="h-5 w-5 text-disabled" />
          </div>
          <input
            id={startDateId}
            type="date"
            value={startDate || ''}
            onChange={handleStartDateChange}
            disabled={disabled}
            className="block w-full pl-10 pr-3 py-3 rounded-lg bg-surface border border-interactive
              text-main
              focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent
              transition-all shadow-sm hover:border-interactive-hover
              disabled:opacity-50 disabled:cursor-not-allowed
              [color-scheme:light]"
          />
        </div>
      </div>

      {/* End Date Input */}
      <div className="flex-1">
        <label htmlFor={endDateId} className="block text-xs  text-muted mb-1.5">
          End Date
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <CalendarIcon className="h-5 w-5 text-disabled" />
          </div>
          <input
            id={endDateId}
            type="date"
            value={endDate || ''}
            onChange={handleEndDateChange}
            disabled={disabled}
            min={startDate || undefined}
            className="block w-full pl-10 pr-3 py-3 rounded-lg bg-surface border border-interactive
              text-main
              focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent
              transition-all shadow-sm hover:border-interactive-hover
              disabled:opacity-50 disabled:cursor-not-allowed
              [color-scheme:light]"
          />
        </div>
      </div>
    </div>
  );
};
